import type { ChecklistItem } from '@/types/api'
import { needsCdds10Submit, persyaratanCddsHref, type CddsNavContext } from '@/lib/cdds-rules'

export { persyaratanCddsHref }

export const CHECKLIST_STEP_KEYS = [
  'email_verified',
  'form_perjanjian_filled',
  'persyaratan_accepted',
  'live_account_created',
  'perjanjian_accepted',
  'deposit_funds',
] as const

export type { CddsNavContext }

export type ChecklistStepKey = (typeof CHECKLIST_STEP_KEYS)[number]

export function mergeChecklistItems(items: ChecklistItem[]): ChecklistItem[] {
  const completedByKey = new Map(items.map((item) => [item.key, item.completed]))
  return CHECKLIST_STEP_KEYS.map((key) => ({
    key,
    completed: completedByKey.get(key) ?? false,
  }))
}

export function onboardingProgress(items: ChecklistItem[]): { completed: number; total: number } {
  const steps = mergeChecklistItems(items)
  const completed = steps.filter((s) => s.completed).length
  return { completed, total: steps.length }
}

/** First incomplete step link for blocked-state CTAs (live modal uses dashboard + ?live=1). */
export function getNextOnboardingHref(
  items: ChecklistItem[],
  clientId: string | null,
  cddsNav?: CddsNavContext,
): string | null {
  if (needsCdds10Submit(cddsNav ?? {})) {
    return '/register/cdds/10'
  }

  const steps = mergeChecklistItems(items)
  const first = steps.find((s) => !s.completed)
  if (!first) return null

  const key = first.key as ChecklistStepKey
  switch (key) {
    case 'email_verified':
      return '/verify-email'
    case 'live_account_created':
      return '/dashboard?live=1'
    case 'form_perjanjian_filled':
      return '/register/data'
    case 'persyaratan_accepted':
      return '/register/cdds'
    case 'perjanjian_accepted':
      return '/dashboard'
    case 'deposit_funds':
      return '/funding'
    default:
      return '/dashboard'
  }
}

export function getFirstIncompleteChecklistKey(items: ChecklistItem[]): ChecklistStepKey | null {
  const steps = mergeChecklistItems(items)
  const first = steps.find((s) => !s.completed)
  return first ? (first.key as ChecklistStepKey) : null
}

/** CTA when getNextOnboardingHref is null or waiting-only step. */
export function getOnboardingCurrentStepIndex(
  steps: ChecklistItem[],
  opts: { awaitingApproval?: boolean; cddsNav?: CddsNavContext; isLoading?: boolean },
): number {
  if (opts.isLoading) return -1
  if (needsCdds10Submit(opts.cddsNav ?? {})) {
    const idx = steps.findIndex((item) => item.key === 'persyaratan_accepted')
    if (idx >= 0) return idx
  }
  const approvalIndex = steps.findIndex((item) => item.key === 'perjanjian_accepted' && !item.completed)
  if (opts.awaitingApproval && approvalIndex >= 0) return approvalIndex
  return steps.findIndex((item) => !item.completed)
}

export function getBlockedOnboardingFallbackHref(
  items: ChecklistItem[],
  clientId: string | null,
  cddsNav?: CddsNavContext,
): string {
  const href = getNextOnboardingHref(items, clientId, cddsNav)
  if (href) return href
  const key = getFirstIncompleteChecklistKey(items)
  if (key === 'form_perjanjian_filled') return '/register/data'
  return '/dashboard'
}
